import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { listAllEmployees, peopleGateway } from '../../people';
import type { EmployeeResponse } from '../../people/types';
import { jobRoleGateway } from '../index';
import { isForbidden, jobRoleErrorMessage } from '../jobRoleErrors';
import { ModalDialog } from './ModalDialog';

const TERMINATED = 2;

const todayIso = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export interface AssignEmployeesDialogProps {
  role: { id: string; title: string };
  onClose: () => void;
  /** Called once at least one employee was assigned, with how many. */
  onAssigned: (count: number) => void;
  onForbidden?: () => void;
}

/**
 * Assign Employees: pick current employees (names and numbers only, never pay) and give each the role from an effective date.
 * Each assignment is its own request (`POST /api/employees/{id}/job-role-assignments`), ends that employee's previous primary role
 * and keeps the history. Only an Active role can be assigned: the server refuses a Draft or Archived one. Assigning a job role
 * never changes what anyone can do in EPN.
 */
export function AssignEmployeesDialog({ role, onClose, onAssigned, onForbidden }: AssignEmployeesDialogProps) {
  const uid = useId();
  const [employees, setEmployees] = useState<EmployeeResponse[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [effectiveDate, setEffectiveDate] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState<string[]>([]);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    listAllEmployees(peopleGateway)
      .then((all) => { if (!cancelled) setEmployees(all.filter((e) => e.status !== TERMINATED)); })
      .catch((err: unknown) => { if (!cancelled) setLoadError(jobRoleErrorMessage(err, 'Could not load employees.')); });
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (employees ?? []).filter((e) => !q || `${e.firstName} ${e.lastName} ${e.employeeNumber}`.toLowerCase().includes(q));
  }, [employees, search]);

  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  async function submit() {
    if (selected.size === 0) {
      setError('Choose at least one employee.');
      errorRef.current?.focus();
      return;
    }
    if (effectiveDate > todayIso()) {
      setError('The effective date cannot be in the future. Leave it blank to start today.');
      errorRef.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    setFailures([]);
    let assigned = 0;
    const failed: { id: string; text: string }[] = [];
    let refused = false;
    for (const id of selected) {
      const employee = employees?.find((e) => e.id === id);
      const name = employee ? `${employee.firstName} ${employee.lastName}` : 'An employee';
      try {
        await jobRoleGateway.assignEmployeeRole(id, {
          jobTitleId: role.id,
          ...(effectiveDate ? { effectiveFromDate: effectiveDate } : {}),
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          // One key per attempt: it protects a retried network call, never a deliberate second try.
          idempotencyKey: crypto.randomUUID(),
        });
        assigned += 1;
      } catch (err) {
        if (isForbidden(err)) {
          refused = true;
          onForbidden?.();
          break;
        }
        failed.push({ id, text: `${name}: ${jobRoleErrorMessage(err, 'could not be assigned.')}` });
      }
    }
    setBusy(false);
    if (refused) {
      setError('You do not have permission to assign roles to employees. Ask a company administrator if you need access.');
      return;
    }
    if (assigned > 0) onAssigned(assigned);
    if (failed.length > 0) {
      setFailures(failed.map((f) => f.text));
      setError(assigned > 0 ? `${assigned} assigned. ${failed.length} could not be assigned:` : 'No one could be assigned:');
      // Keep the people that failed selected so they can try again; the ones that worked are done.
      setSelected(new Set(failed.map((f) => f.id)));
      return;
    }
    if (assigned > 0) onClose();
  }

  return (
    <ModalDialog
      title={`Assign employees to ${role.title}`}
      size="md"
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="jr-btn jr-btn--primary" onClick={() => void submit()} disabled={busy || employees === null}>
            {busy ? 'Assigning…' : `Assign ${selected.size > 0 ? selected.size : ''} ${selected.size === 1 ? 'employee' : 'employees'}`.replace(/\s+/g, ' ')}
          </button>
        </>
      )}
    >
      <p className="muted">
        This sets each person&apos;s job role. Their previous role is kept in their history. It does not change their pay or what they can do in EPN.
      </p>
      {error && (
        <div ref={errorRef} tabIndex={-1} role="alert" className="jr-error jr-error--block">
          <p>{error}</p>
          {failures.length > 0 && <ul>{failures.map((f) => <li key={f}>{f}</li>)}</ul>}
        </div>
      )}
      {loadError && <p className="jr-error" role="alert">{loadError}</p>}
      {employees === null && !loadError && <p className="muted" role="status">Loading employees{'…'}</p>}
      {employees !== null && (
        <>
          <div className="jr-field">
            <label htmlFor={`${uid}-search`}>Find an employee</label>
            <input id={`${uid}-search`} data-autofocus type="search" value={search} onChange={(e) => setSearch(e.target.value)} autoComplete="off" />
          </div>
          <fieldset className="jr-pick">
            <legend>Employees</legend>
            {visible.length === 0 && <p className="muted">No employees match.</p>}
            {visible.map((e) => {
              const already = e.jobTitleId === role.id;
              return (
                <label key={e.id} className="jr-pick__row">
                  <input type="checkbox" checked={selected.has(e.id)} disabled={already || busy} onChange={() => toggle(e.id)} />
                  <span>
                    {e.firstName} {e.lastName} <span className="muted">({e.employeeNumber})</span>
                    {already && <span className="muted"> {'—'} already in this role</span>}
                  </span>
                </label>
              );
            })}
          </fieldset>
          <div className="jr-grid">
            <div className="jr-field">
              <label htmlFor={`${uid}-date`}>Effective date</label>
              <input id={`${uid}-date`} type="date" max={todayIso()} value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} disabled={busy} />
              <p className="jr-hint">Leave blank to start today. It cannot be in the future.</p>
            </div>
            <div className="jr-field">
              <label htmlFor={`${uid}-reason`}>Reason (optional)</label>
              <input id={`${uid}-reason`} type="text" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} />
            </div>
          </div>
        </>
      )}
    </ModalDialog>
  );
}
